import { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
  Tooltip,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortIcon from '@mui/icons-material/Sort';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { OrderDirection } from '@enums/orderDirectionEnum';

interface SortableColumn {
  key: string;
  label: string;
  lowLabel: string;
  highLabel: string;
}

interface DrawRequestSortMenuProps {
  currentOrder?: { name: string; direction: OrderDirection };
  onSortChange: (name: string, direction: OrderDirection) => void;
}

export default function DrawRequestSortMenu({
  currentOrder,
  onSortChange,
}: DrawRequestSortMenuProps) {
  const sortableColumns = useMemo<SortableColumn[]>(
    () => [
      {
        key: 'dateCreated',
        label: 'Date',
        lowLabel: 'Oldest to Newest',
        highLabel: 'Newest to Oldest',
      },
      {
        key: 'drawRequestId',
        label: 'Request Id',
        lowLabel: 'Low to High',
        highLabel: 'High to Low',
      },
      {
        key: 'requestAmount',
        label: 'Draw Amount',
        lowLabel: 'Low to High',
        highLabel: 'High to Low',
      },
      {
        key: 'budgetAmount',
        label: 'Budgeted Amount',
        lowLabel: 'Low to High',
        highLabel: 'High to Low',
      },
      {
        key: 'status',
        label: 'Status',
        lowLabel: 'A → Z',
        highLabel: 'Z → A',
      },
    ],
    []
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const sortMenuOpen = Boolean(sortMenuAnchorEl);

  const openMenu = (e: React.MouseEvent<HTMLElement>) =>
    setMenuAnchorEl(e.currentTarget);
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setSortMenuAnchorEl(null);
  };

  const openSortMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setSortMenuAnchorEl(e.currentTarget);
  };

  const closeSortMenu = () => {
    setSortMenuAnchorEl(null);
    setMenuAnchorEl(null);
  };

  const handleMenuClose = (_event: {}, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      closeMenu();
      closeSortMenu();
      return;
    }
    if (reason === 'menuItemClick' && sortMenuAnchorEl) {
      return;
    }
  };

  const applySort = (name: string, direction: OrderDirection) => {
    onSortChange(name, direction);
    closeMenu();
    closeSortMenu();
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton aria-label="more" onClick={openMenu}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        MenuListProps={{
          'aria-labelledby': 'actions-menu',
          onMouseLeave: () => {
            if (!sortMenuOpen) {
              setTimeout(() => {
                if (!sortMenuOpen) {
                  closeMenu();
                }
              }, 200);
            }
          },
        }}
      >
        <MenuItem onClick={openSortMenu} onMouseEnter={openSortMenu}>
          <SortIcon fontSize="small" style={{ marginRight: 8 }} />
          <ListItemText primary="Sort" />
          <ChevronRightIcon fontSize="small" style={{ marginLeft: 8 }} />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={sortMenuAnchorEl}
        open={sortMenuOpen}
        onClose={(_event: {}, reason: string) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeSortMenu();
          }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
      >
        <ListSubheader
          disableSticky
          sx={{
            lineHeight: 1.5,
            py: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SortIcon fontSize="small" /> Sort by
        </ListSubheader>
        <Divider />

        {sortableColumns.map(col => (
          <Box key={col.key}>
            <MenuItem
              onClick={() => applySort(col.key, OrderDirection.ASC)}
              selected={
                currentOrder?.name === col.key &&
                currentOrder?.direction === OrderDirection.ASC
              }
            >
              <ArrowUpwardIcon fontSize="small" style={{ marginRight: 8 }} />
              <ListItemText
                primary={
                  <>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {col.label}:
                    </Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {col.lowLabel}
                    </Box>
                  </>
                }
              />
            </MenuItem>
            <MenuItem
              onClick={() => applySort(col.key, OrderDirection.DESC)}
              selected={
                currentOrder?.name === col.key &&
                currentOrder?.direction === OrderDirection.DESC
              }
            >
              <ArrowDownwardIcon fontSize="small" style={{ marginRight: 8 }} />
              <ListItemText
                primary={
                  <>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {col.label}:
                    </Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {col.highLabel}
                    </Box>
                  </>
                }
              />
            </MenuItem>
            <Divider />
          </Box>
        ))}
      </Menu>
    </>
  );
}
